import { apiRole } from "@/api/role.api.ts";
import {
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Box,
  Button,
  Checkbox,
  Radio,
  Skeleton,
  Stack,
  Text,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useRoleById, useRoles, useRolesSave } from "@/queries/settings/use-roles.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiRoleSavePayload, PermissionType, RoleType } from "@/types/role.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

interface ApplyRoleSettingsToAnotherRoleProps extends ModalProps {
  roleId: number;
}

export function ApplyRoleSettingsToAnotherRole({
  roleId,
  ...props
}: ApplyRoleSettingsToAnotherRoleProps) {
  const { id } = props;
  const { close } = useModals();
  const { t } = useTranslation();
  const { data: roles, isLoading: rolesIsLoading } = useRoles();
  const { data: role } = useRoleById(roleId);
  const navigate = useNavigate();
  const { showInfo } = useToasts();
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const ApplyRoleSettingsToAnotherRoleSchemaObject = z.object({
    is_new_role: z.boolean().optional(),
    role_ids_for_update: z.array(z.number()).optional(),
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(ApplyRoleSettingsToAnotherRoleSchemaObject),
    defaultValues: {
      is_new_role: true,
    },
  });

  const roleSave = useRolesSave(control);
  const isNewRole = watch("is_new_role");
  const roleIdsForUpdate = watch("role_ids_for_update");
  const getEmployeeWorkflowPermissionsPayload = () =>
    role?.employee_workflow_permissions.reduce(
      (acc, workflow) => {
        acc[workflow.workflow_id] = { run: workflow.run, executions: workflow.executions };
        return acc;
      },
      {} as Record<number, { run: PermissionType; executions: PermissionType }>
    ) ?? {};

  const confirmation = handleSubmit(async (data) => {
    if (!role) return;

    if (isNewRole) {
      const payload: ApiRoleSavePayload = {
        title: `${role.title} (${t("core:copy")})`,
        description: "",
        assign_to: [],
        apply_to: [],
        api_permissions: role.api_permissions,
        employee_field_permissions: role.employee_field_permissions,
        employee_custom_field_permissions: role.employee_custom_field_permissions.reduce(
          (acc, field) => {
            acc[field.slug] = field.permission_value;

            return acc;
          },
          {} as Record<string, PermissionType>
        ),
        employee_custom_table_permissions: role.employee_custom_table_permissions.map((table) => ({
          table_id: table.table_id,
          manage_table: table.manage_table,
          history: table.history,
          effective_from: table.effective_from,
          changed_by: table.changed_by,
          field_permissions: table.field_permissions.reduce(
            (acc, field) => ({
              ...acc,
              [field.slug]: field.permission_value,
            }),
            {} as Record<string, PermissionType>
          ),
        })),
        employee_folder_permissions: role.employee_folder_permissions.reduce(
          (acc, fp) => {
            acc[fp.folder_id] = fp.permission_value;
            return acc;
          },
          {} as Record<number, PermissionType>
        ),
        employee_workflow_permissions: getEmployeeWorkflowPermissionsPayload(),
      };

      roleSave.mutate(
        { id: 0, payload },
        {
          onSuccess(createdRole) {
            navigate(`/settings/roles-and-permissions/${createdRole.id}`);

            analyticsService.trackEvent(analyticEvents.roles.created, {
              [analyticProperties.id]: createdRole.id,
            });

            close(id);
          },
        }
      );
    } else {
      const ids = data.role_ids_for_update ?? [];

      if (ids.length == 0) {
        close(id);
        return;
      }

      setIsBulkSaving(true);

      try {
        const tasks = ids.map(async (roleIdToCopy) => {
          if (!roleIdToCopy) return { status: "skipped" } as const;

          const { data: roleTo } = await apiRole.getById(roleIdToCopy);

          if (!roleTo) return { status: "skipped" } as const;

          const payload: ApiRoleSavePayload = {
            ...roleTo,
            employee_field_permissions: role.employee_field_permissions,
            api_permissions: role.api_permissions,
            employee_custom_field_permissions: role.employee_custom_field_permissions.reduce(
              (acc, field) => {
                acc[field.slug] = field.permission_value;

                return acc;
              },
              {} as Record<string, PermissionType>
            ),
            employee_custom_table_permissions: role.employee_custom_table_permissions.map(
              (table) => ({
                table_id: table.table_id,
                manage_table: table.manage_table,
                history: table.history,
                effective_from: table.effective_from,
                changed_by: table.changed_by,
                field_permissions: table.field_permissions.reduce(
                  (acc, field) => ({
                    ...acc,
                    [field.slug]: field.permission_value,
                  }),
                  {} as Record<string, PermissionType>
                ),
              })
            ),
            employee_folder_permissions: role.employee_folder_permissions.reduce(
              (acc, fp) => {
                acc[fp.folder_id] = fp.permission_value;
                return acc;
              },
              {} as Record<number, PermissionType>
            ),
            employee_workflow_permissions: getEmployeeWorkflowPermissionsPayload(),
          };

          await roleSave.mutateAsync({ id: roleTo.id, payload });

          analyticsService.trackEvent(analyticEvents.roles.edited, {
            [analyticProperties.id]: roleTo.id,
          });

          showInfo({ title: t("core:role_updated", { role_name: roleTo.title }) });

          return { status: "fulfilled", id: roleTo.id } as const;
        });

        await Promise.allSettled(tasks);
      } finally {
        setIsBulkSaving(false);
        close(id);
      }
    }
  });

  return (
    <Modal
      title={t("core:apply_settings_to_another_role")}
      {...props}
      withCloser={true}
      layout={"stretch"}
      actions={
        <Stack gap={"sm"} className={"text-center"}>
          <ServerErrorField errors={errors} />
          <Button size={"lg"} onClick={confirmation} isLoading={roleSave.isPending || isBulkSaving}>
            {isNewRole
              ? t("core:confirm_and_copy")
              : t("core:update_settings_in_x_roles", {
                  in_count_roles: t("plurals:in_role_count", { count: roleIdsForUpdate?.length }),
                })}
          </Button>
          <Text>{t("core:warning_cant_undo")}</Text>
        </Stack>
      }
    >
      <Stack gap={"sm"} className={"select-none"}>
        <Stack className={"px-4 py-2"}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Radio
                checked={value}
                onChange={() => {
                  onChange(true);
                }}
                title={t("core:create_role_from_settings_title")}
                caption={t("core:create_role_from_settings_caption")}
              />
            )}
            name={"is_new_role"}
            control={control}
          />
        </Stack>
        <Stack className={twMerge("px-4 py-2 card-post shadow-md")}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Radio
                checked={!value}
                onChange={() => {
                  onChange(false);
                }}
                title={t("core:update_role_from_settings_title")}
                caption={t("core:update_role_from_settings_caption")}
              />
            )}
            name={"is_new_role"}
            control={control}
          />
          {!isNewRole && (
            <Controller
              control={control}
              name="role_ids_for_update"
              render={({ field: { value, onChange } }) => (
                <Stack className={"mb-2"}>
                  {rolesIsLoading && (
                    <Box className={"border-t py-4"}>
                      <Skeleton className={"h-5 w-full"} />
                    </Box>
                  )}
                  {roles
                    ?.filter((role) => role.type === RoleType.custom && role.id != roleId)
                    .map((role, index) => (
                      <Box
                        className={twMerge("py-4 border-b", index == 0 ? "border-t" : "")}
                        key={`role-item-${role.id}`}
                      >
                        <Checkbox
                          checked={value?.includes(role.id)}
                          onChange={() => {
                            if (value?.includes(role.id)) {
                              onChange(value.filter((item) => item != role.id));
                            } else {
                              onChange([...(value ?? []), role.id]);
                            }
                          }}
                          title={role.title}
                        />
                      </Box>
                    ))}
                </Stack>
              )}
            />
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}
